import { cn } from '@/utils/cn'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { type VariantProps, cva } from 'class-variance-authority'
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react'

const switchVariants = cva(
  cn(
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full bg-input-disabled outline-none transition-colors',
    'focus-visible:ring-2',
    'data-[state=checked]:bg-accent',
    'disabled:cursor-not-allowed disabled:bg-input-disabled data-[state=checked]:disabled:bg-input-disabled',
    '[&:disabled>.switch-thumb]:bg-disabled'
  ),
  {
    variants: {
      size: {
        md: cn('h-6 w-10 p-0.5 [&>.switch-thumb]:size-5', '[&[data-state="checked"]_.switch-thumb]:translate-x-4'),
        sm: cn('h-5 w-9 p-0.5 [&>.switch-thumb]:size-4', '[&[data-state="checked"]_.switch-thumb]:translate-x-4'),
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export type SwitchProps = ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & VariantProps<typeof switchVariants>

export const Switch = forwardRef<ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ size, className, ...props }, ref) => (
    <SwitchPrimitives.Root className={cn(switchVariants({ size, className }))} {...props} ref={ref}>
      <SwitchPrimitives.Thumb
        data-role="thumb"
        className={cn(
          'switch-thumb',
          'pointer-events-none block rounded-full bg-button-foreground transition-transform'
        )}
      />
    </SwitchPrimitives.Root>
  )
)
Switch.displayName = SwitchPrimitives.Root.displayName
